/**
 * 安全防护信号采集（与 iOS RegionContextService 对齐）。
 *
 * Cloudflare 在边缘按 Header 做地区拦截，且不区分 iOS / Web，
 * 因此 Web 采集不到的字段（Store / SIM / VPN / Language / Region / Timezone）
 * 一律用美国默认值补齐，避免命中 iOS 的 unknown=CN 规则被误拦。
 */
import { logAnalyticsEvent, refreshAnalyticsUserProperties } from '@/assets/js/firebase'

/** Web 固定占位值（采集不到，避免 unknown 误判为 CN） */
const STORE_COUNTRY = 'USA'
const SIM_MCC = '310'
const VPN_CONNECTED = 'false'

/** Web 固定构建号（仅为与 iOS 对齐，Web 不参与审核放行） */
const APP_BUILD = '0'

/** Web 固定美国地区信号 */
const DEFAULT_LANGUAGE = 'en-US'
const DEFAULT_REGION = 'US'
const DEFAULT_TIMEZONE = 'America/New_York'

/** API Header 名（与 iOS 一致） */
export const SECURITY_HEADERS = {
  APP_BUILD: 'X-App-Build',
  STORE_COUNTRY: 'X-Store-Country',
  SIM_MCC: 'X-Sim-MCC',
  DEVICE_LANGUAGE: 'X-Device-Language',
  DEVICE_REGION: 'X-Device-Region',
  DEVICE_TIMEZONE: 'X-Device-Timezone',
  VPN_CONNECTED: 'X-Vpn-Connected'
}

let cachedSignals = null

/** 采集一次并缓存（同一会话内信号稳定） */
export function collectSecuritySignals ({ refresh = false } = {}) {
  if (cachedSignals && !refresh) return cachedSignals

  cachedSignals = {
    appBuild: APP_BUILD,
    storeCountry: STORE_COUNTRY,
    simMcc: SIM_MCC,
    deviceLanguage: DEFAULT_LANGUAGE,
    deviceRegion: DEFAULT_REGION,
    deviceTimezone: DEFAULT_TIMEZONE,
    vpnConnected: VPN_CONNECTED
  }
  return cachedSignals
}

/** 供 request.js 注入的 Header 映射 */
export function getSecurityHeaders () {
  const s = collectSecuritySignals()
  return {
    [SECURITY_HEADERS.APP_BUILD]: s.appBuild,
    [SECURITY_HEADERS.STORE_COUNTRY]: s.storeCountry,
    [SECURITY_HEADERS.SIM_MCC]: s.simMcc,
    [SECURITY_HEADERS.DEVICE_LANGUAGE]: s.deviceLanguage,
    [SECURITY_HEADERS.DEVICE_REGION]: s.deviceRegion,
    [SECURITY_HEADERS.DEVICE_TIMEZONE]: s.deviceTimezone,
    [SECURITY_HEADERS.VPN_CONNECTED]: s.vpnConnected
  }
}

/** 供 Firebase 用户属性 / 埋点使用（字段名与 iOS 对齐） */
export function getSecuritySignalsForAnalytics () {
  const s = collectSecuritySignals()
  return {
    store_country: s.storeCountry,
    device_region: s.deviceRegion,
    device_language: s.deviceLanguage,
    device_timezone: s.deviceTimezone,
    sim_mcc: s.simMcc,
    vpn_connected: s.vpnConnected
  }
}

/** 启动时采集信号，写入 Firebase 用户属性并上报一次 region_context 事件 */
export function initSecuritySignals () {
  collectSecuritySignals()
  refreshAnalyticsUserProperties()
  logAnalyticsEvent('region_context', getSecuritySignalsForAnalytics())
}
